import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Radio } from 'antd';

import { DataState, DATA_STATE, Skeleton } from '../loading';

import { formatCount } from '../../utils/counts';
import { itemTagRefusal } from '../../utils/itemTagSettings';

/**
 * THE OFF DIALOG'S PIECES, shared by its two doors (docs/plans/tags.md §3.2,
 * ruled d30, d36; owner call B19; build step T4d and its API follow-ups): a
 * card's Off in ItemTagModeModal and one item's Stop using as a tag in
 * StopItemTagModal. Both ask the same two questions with the same counts,
 * and both show the names a keep publishes before it is sent.
 *
 * - `OffChoiceRow` — one question (**Applied by a rule** / **Put on by
 *   hand**), how many items it touches from the dry run's `totals`, and
 *   **Remove** · **Keep**.
 * - `useKeptNames(dryRun)` — the names a keep publishes: a dry run with the
 *   choices answers `names: [{ itemId, tagId, name }]` and `confirmNames`, and
 *   the real call echoes that digest (409 `confirm_names_required` when the
 *   names moved since: `load` again with `again: true`). Only the latest ask
 *   lands; `key` is the body it was asked with, so a caller can tell a digest
 *   that no longer matches what it would send.
 * - `KeptNames` — that state on screen: loading, the refusal, "changed since
 *   you looked", then the caller's rendering of the list.
 */

/** A choice row's count, as the dry run's totals give it; `fallback` before they land. */
export function offChoiceHint(count, fallback = null) {
	if (count === null || count === undefined) return fallback;
	const n = Number(count) || 0;
	return n ? `On ${formatCount(n, 'item')}.` : 'On no items yet.';
}

export function OffChoiceRow({ label, count, fallbackHint = null, value, onChange, disabled = false, testId }) {
	const hint = offChoiceHint(count, fallbackHint);
	return (
		<div className="stop-item-tag__choice" data-testid={testId}>
			<div className="stop-item-tag__choice-text">
				<span className="stop-item-tag__choice-label">{label}</span>
				{hint ? <span className="stop-item-tag__choice-hint">{hint}</span> : null}
			</div>
			<div role="radiogroup" aria-label={label} className="tag-choice">
				<Radio.Group
					optionType="button"
					value={value}
					disabled={disabled}
					onChange={event => onChange(event.target.value)}
					options={[
						{ label: 'Remove', value: 'remove' },
						{ label: 'Keep', value: 'keep' },
					]}
				/>
			</div>
		</div>
	);
}

/**
 * `useKeptNames(dryRun)` → `{ names, load, clear }`. `dryRun(body)` resolves
 * the dry run's answer. `names` is null, or `{ loading, error, list, digest,
 * again, key }` (`error` the raw refusal). `load(body, { again, key })`
 * resolves the answer (null when refused or overtaken).
 */
export function useKeptNames(dryRun) {
	const [names, setNames] = useState(null);
	const seq = useRef(0);
	const live = useRef(true);
	const run = useRef(dryRun);
	run.current = dryRun;

	useEffect(() => {
		live.current = true;
		return () => { live.current = false; };
	}, []);

	const load = useCallback(async function load(body, { again = false, key = null } = {}) {
		const mine = ++seq.current;
		setNames({ loading: true, error: null, list: [], digest: null, again, key });
		try {
			const answer = await run.current(body);
			if (!live.current || mine !== seq.current) return null;
			setNames({
				loading: false,
				error: null,
				list: Array.isArray(answer?.names) ? answer.names : [],
				digest: answer?.confirmNames || null,
				again,
				key,
			});
			return answer || {};
		} catch (error) {
			if (!live.current || mine !== seq.current) return null;
			setNames({ loading: false, error, list: [], digest: null, again, key, retry: () => load(body, { again, key }) });
			return null;
		}
	}, []);

	const clear = useCallback(() => {
		seq.current += 1;
		setNames(null);
	}, []);

	return { names, load, clear };
}

/** "The name(s) changed since you looked": one name or many. */
export const namesAgainWords = count => (count === 1
	? 'The name changed since you looked. Here it is again.'
	: 'The names changed since you looked. Here they are again.');

export function KeptNames({ names, className, testId, errorFallback = 'The names could not be listed. Please try again.', children }) {
	if (!names) return null;
	return (
		<div className={className} data-testid={testId}>
			{names.loading ? <DataState state={DATA_STATE.LOADING} skeleton={<Skeleton variant="line" count={2} />} label="the names" /> : null}
			{!names.loading && names.error ? (
				<DataState
					state={DATA_STATE.ERROR}
					label="the names"
					error={itemTagRefusal(names.error, errorFallback)}
					onRetry={names.retry}
				/>
			) : null}
			{!names.loading && !names.error ? (
				<>
					{names.again ? <Alert type="warning" showIcon message={namesAgainWords(names.list.length)} /> : null}
					{typeof children === 'function' ? children(names.list) : children}
				</>
			) : null}
		</div>
	);
}
